<template>
  <div class="crowded-stages" :class="`crowded-stages--${variant}`">
    <template v-for="(stage, i) in items">
      <span
        v-if="i > 0 && variant === 'tiles'"
        :key="`${stage.key}-arrow`"
        class="crowded-stages__arrow"
        aria-hidden="true"
        >→</span
      >
      <div
        :key="stage.key"
        v-b-tooltip.hover
        :title="stage.help"
        class="crowded-stages__stage"
        :class="{
          'crowded-stages__stage--empty': !stage.value,
          'crowded-stages__stage--highlight': stage.key === 'available' && stage.value,
        }"
      >
        <span class="crowded-stages__label">{{ stage.label }} ⓘ</span>
        <span class="crowded-stages__value">
          <span class="crowded-stages__amount">{{ stage.value || zero }}</span>
          <span v-if="variant === 'tiles' && stage.qualifier" class="crowded-stages__qualifier">{{
            stage.qualifier
          }}</span>
        </span>
        <span v-if="variant === 'tiles'" class="crowded-stages__note">{{ stage.note }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { addBalanceAmounts, formatBalanceAmounts, formatMoney } from "@/helpers/format-money.js";

// Crowded money moves Processing (charged, then batched and in transit to
// Crowded) -> Releasing soon (settled in the hold account, waiting for Charidy
// to release it) -> Available (released, ready to pay out). Empty stages stay
// visible, greyed, so the layout doesn't shift. The compact "row" (gateway
// list header) folds the first two into one Pending figure; the details view
// shows all three as tiles.
export default {
  props: {
    balance: { type: Object, default: null },
    // Crowded payout account from the store: decides the Available note.
    payoutAccount: { type: Object, default: null },
    // "row" for the gateway list header, "tiles" for the gateway details.
    variant: { type: String, default: "row" },
  },

  computed: {
    items() {
      if (this.variant === "tiles") {
        return this.stages;
      }
      const b = this.balance || {};
      const [processing, releasing, available] = this.stages;
      const pending = formatBalanceAmounts(addBalanceAmounts(b.captured, b.in_transit, b.pending), {
        skipZero: true,
      });
      const parts = [
        processing.value &&
          this.$t("gateways.crowded_stage_pending_processing", "{amount} processing", {
            amount: processing.value,
          }),
        releasing.value &&
          this.$t("gateways.crowded_stage_pending_releasing", "{amount} releasing soon", {
            amount: releasing.value,
          }),
      ].filter(Boolean);
      return [
        {
          key: "pending",
          label: this.$t("gateways.crowded_stage_pending", "Pending"),
          value: pending,
          help: [
            this.$t("gateways.crowded_stage_pending_help", "Donations on their way to you."),
            parts.join(", "),
            this.$t("gateways.crowded_stage_pending_before_fees", "Amounts are before fees."),
          ]
            .filter(Boolean)
            .join(" "),
        },
        available,
      ];
    },

    zero() {
      return formatMoney(0, "usd");
    },

    stages() {
      const b = this.balance || {};
      const fmt = (amounts) => formatBalanceAmounts(amounts, { skipZero: true });
      const captured = fmt(b.captured);
      const inTransit = fmt(b.in_transit);
      const beforeFees = this.$t("gateways.crowded_stage_before_fees", "(before fees)");
      const processingHelp = [
        this.$t(
          "gateways.crowded_stage_processing_help",
          "Donations being settled by the card processor."
        ),
        captured &&
          this.$t("gateways.crowded_stage_captured", "{amount} charged, waiting to be batched", {
            amount: captured,
          }),
        inTransit &&
          this.$t(
            "gateways.crowded_stage_in_transit",
            "{amount} batched, on the way to your Crowded account",
            { amount: inTransit }
          ),
      ]
        .filter(Boolean)
        .join(" ");

      return [
        {
          key: "processing",
          label: this.$t("gateways.crowded_stage_processing", "Processing"),
          value: fmt(addBalanceAmounts(b.captured, b.in_transit)),
          help: processingHelp,
          note: this.$t("gateways.crowded_stage_processing_note", "1-2 business days"),
          qualifier: beforeFees,
        },
        {
          key: "releasing",
          label: this.$t("gateways.crowded_stage_releasing", "Releasing soon"),
          value: fmt(b.pending),
          help: this.$t(
            "gateways.crowded_stage_releasing_help",
            "Settled funds that Charidy will release to your account."
          ),
          note: this.$t("gateways.crowded_stage_releasing_note", "Within 1 calendar day"),
          qualifier: beforeFees,
        },
        {
          key: "available",
          label: this.$t("gateways.crowded_stage_available", "Available"),
          value: fmt(b.available),
          help: this.$t(
            "gateways.crowded_stage_available_help",
            "Released to you and ready to pay out to your bank account."
          ),
          note: this.availableNote,
          qualifier: this.$t("gateways.crowded_stage_after_fees", "(after all fees)"),
        },
      ];
    },

    availableNote() {
      const account = this.payoutAccount;
      if (!account || account.status === "missing") {
        return this.$t("gateways.crowded_stage_available_note", "Ready to pay out");
      }
      return account.enabled
        ? this.$t("gateways.crowded_stage_available_auto_note", "Paid out automatically each day")
        : this.$t("gateways.crowded_stage_available_paused_note", "Automatic payouts paused");
    },
  },
};
</script>

<style lang="scss" scoped>
.crowded-stages {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  &__arrow {
    align-self: center;
    color: #bbbcc4;

    @media (max-width: 767px) {
      display: none;
    }
  }

  &__stage {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 14px;
    color: #bbbcc4;
  }

  &__value {
    font-size: 14px;
    font-weight: 700;
    color: #414359;
  }

  &__amount {
    white-space: nowrap;
  }

  &__stage--empty &__value {
    color: #bbbcc4;
    font-weight: 400;
  }

  &--tiles {
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 1rem;

    @media (max-width: 767px) {
      flex-direction: column;
    }

    .crowded-stages__stage {
      flex: 1;
      min-width: 0;
      padding: 10px 12px;
      background-color: #f5f6f8;
      border: 1px solid #e6e8ec;
      border-radius: 8px;
    }

    .crowded-stages__stage--highlight {
      background-color: #eaf4fb;
      border-color: #b9dcf3;
    }

    .crowded-stages__label {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6f7a8a;
    }

    .crowded-stages__value {
      font-size: 16px;
    }

    .crowded-stages__qualifier {
      font-size: 11px;
      white-space: nowrap;
      font-weight: 400;
      color: #6f7a8a;
    }

    .crowded-stages__note {
      font-size: 12px;
      color: #6f7a8a;
    }
  }
}
</style>
